import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';

import {
  OTA_ACCOUNTS_TITLE,
  OTA_ACCOUNTS_NOTE,
  OTA_ACCOUNTS_DETAIL_NOTE,
  OTA_VAT_PLATFORMS,
  OTA_VAT_STATUS_LABELS,
  OTA_VAT_NO_RECORD_LABEL,
  OTA_UNLINKED_REASON_LABELS,
  OTA_RATE_RULE_SUFFIX,
  OTA_RATE_RULE_UNKNOWN_VALUE,
  OTA_RATE_RULE_NONE,
  OTA_RATE_RULE_OTHER,
  OTA_AIRBNB_NOT_READ_NOTE,
  OTA_VAT_NOTE_HINT
} from '../../config/ownerBillingLabels';
import { formatDateLatn } from '../../utils/dateFormatter';
import { riyadhDayIso } from '../../utils/dateValueHelper';
import { getPlatformDisplayName } from '../../utils/platformUtils';
import { percentExact } from '../pricing/pricingFormat';
import { useOtaAccounts, useAddOtaVatChange, useWithdrawOtaVatChange } from '../../hooks/useOtaAccounts';

/**
 * «حسابات المنصات والتسجيل الضريبي» — the owner's OTA accounts on the billing page
 * (tasks/pricing-listing-build-brainstorm.md, slice 1b).
 *
 * READ, never typed: which Airbnb accounts the owner has, their units and the +15% rule — from Channex's
 * Airbnb copy. TYPED: the VAT status, as dated change rows with an evidence note. RECORD-ONLY: nothing on
 * the worksheet reads it for money.
 *
 * Its own query and its own error state: the box is per owner, not per month, and a failure here must
 * never take the worksheet down. Shown on a finalised month too — it is live, never frozen (the note
 * says so) — which is why it is not hidden like the profile button.
 *
 * Replaces the per-platform date rows the profile modal used to hold (room for ONE Airbnb account).
 */

// ⚠️ MUST MATCH BACKEND — be/models/OtaVatChange.js (OTA_VAT_NOTE_MAX / OTA_VAT_ACCOUNT_NAME_MAX /
// OTA_VAT_REASON_MAX). The server refuses anything longer; these only stop the typing early.
const NOTE_MAX = 500;
const NAME_MAX = 120;
const EARLIEST_DATE = '2018-01-01';
const NEW_ACCOUNT = (platform) => `new:${platform}`;

// A figure inside Arabic text: kept left-to-right so "+15%" never reads as "%15+".
const Figure = ({ children }) => <bdi dir="ltr" className="obw-ota__figure">{children}</bdi>;

const errorText = (error, fallback) => error?.response?.data?.error || fallback;

// Unit codes as a list: each code isolated, so "P-0001، P-0002" reads right-to-left in that order
// instead of fusing into one left-to-right run that an Arabic reader takes backwards.
const CodeList = ({ items }) => items.map((item, i) => (
  <React.Fragment key={item.key}>
    {i > 0 && '، '}
    <bdi>{item.code}</bdi>{item.suffix}
  </React.Fragment>
));

// Channex names its Airbnb accounts "Airbnb …" — print the platform only when the name does not.
const accountTitle = (account) => {
  const platform = getPlatformDisplayName(account.platform);
  const name = account.name || 'حساب بلا اسم في Channex';
  return name.toLowerCase().includes(platform.toLowerCase()) ? { platform: null, name } : { platform, name };
};

const accountLabel = (account) => {
  const { platform, name } = accountTitle(account);
  return platform ? `${platform} — ${name}` : name;
};

const RateRule = ({ adjustments }) => {
  // null = no copy of this account at all — say nothing rather than "no increase".
  if (!Array.isArray(adjustments)) return null;
  if (adjustments.length === 0) return <span>{OTA_RATE_RULE_NONE}</span>;
  return adjustments.map((a, i) => {
    const sign = a.mode === 'increase_by_percent' ? '+' : a.mode === 'decrease_by_percent' ? '−' : null;
    if (!sign) return <span key={i}>{OTA_RATE_RULE_OTHER} <Figure>{a.mode} {a.value ?? ''}</Figure></span>;
    if (typeof a.value !== 'number') return <span key={i}>{OTA_RATE_RULE_UNKNOWN_VALUE}</span>;
    return <span key={i}><Figure>{sign}{percentExact(a.value)}</Figure> {OTA_RATE_RULE_SUFFIX}</span>;
  });
};

const CurrentStatus = ({ current }) => (current
  ? (
    <span className="obw-ota__status">
      <strong>{OTA_VAT_STATUS_LABELS[current.vatStatus] || current.vatStatus}</strong>
      {' '}منذ <bdi>{formatDateLatn(current.date)}</bdi>
    </span>
  )
  : <span className="obw-missing">{OTA_VAT_NO_RECORD_LABEL}</span>);

const ChangeRow = ({ change, canManage, onWithdraw }) => (
  <li className={`obw-ota__change${change.withdrawn ? ' is-withdrawn' : ''}`}>
    <div className="obw-ota__change-main">
      <span className="obw-ota__change-what">
        {OTA_VAT_STATUS_LABELS[change.vatStatus] || change.vatStatus} من <bdi>{formatDateLatn(change.date)}</bdi>
      </span>
      <span className="obw-ota__change-note">{change.note}</span>
      <span className="obw-ota__change-by">
        سجّله <bdi>{change.recordedBy || 'غير معروف'}</bdi> في <bdi>{formatDateLatn(change.recordedAt)}</bdi>
        {change.withdrawn && (
          <> — سحبه <bdi>{change.withdrawnBy || 'غير معروف'}</bdi> في <bdi>{formatDateLatn(change.withdrawnAt)}</bdi>: {change.withdrawalReason}</>
        )}
      </span>
    </div>
    {canManage && !change.withdrawn && (
      <button type="button" className="obw-reopen obw-ota__withdraw" onClick={() => onWithdraw(change)}>
        سحب السطر
      </button>
    )}
  </li>
);

// Collapsed (the default): one line — account, its units, VAT status today. The rule, the read time and
// the history are reference, not day-to-day, so they wait behind «عرض التفاصيل».
const Account = ({ account, expanded, canManage, onWithdraw }) => {
  const title = accountTitle(account);
  // A typed Booking.com / Gathern account whose every row was withdrawn (a typo) is history only.
  if (!expanded && account.platform !== 'airbnb' && !account.current) return null;
  return (
    <div className="obw-ota__account">
      <div className="obw-ota__account-head">
        <div className="obw-ota__account-name">
          {title.platform && <span className="obw-ota__platform">{title.platform}</span>}
          {/* Account names mix Arabic and Latin ("Airbnb النزهة") — fixed RTL so the words keep their order. */}
          <bdi dir="rtl">{title.name}</bdi>
          {account.units.length > 0 && (
            <span className="obw-ota__units">
              <CodeList items={account.units.map(u => ({ key: u.code || u.name, code: u.code || u.name }))} />
            </span>
          )}
        </div>
        <CurrentStatus current={account.current} />
      </div>

      {expanded && account.platform === 'airbnb' && (
        <div className="obw-customer__meta obw-ota__facts">
          {account.units.length === 0 && <div>لا توجد وحدات لهذا المالك على الحساب الآن</div>}
          <div><RateRule adjustments={account.rateAdjustments} /></div>
          {account.lastReadAt && (
            <div className={account.stale ? 'obw-missing' : undefined}>
              آخر قراءة من <bdi>Channex</bdi> في <bdi>{formatDateLatn(account.lastReadAt)}</bdi>
              {account.stale && ' — قديمة (أكثر من يومين)'}
            </div>
          )}
        </div>
      )}

      {expanded && account.changes.length > 0 && (
        <ul className="obw-ota__changes">
          {account.changes.map(c => (
            <ChangeRow key={c.id} change={c} canManage={canManage} onWithdraw={onWithdraw} />
          ))}
        </ul>
      )}
    </div>
  );
};

/** Accounts the add form can target: every listed account, then "a new Booking.com / Gathern account". */
const accountOptions = (accounts) => [
  ...accounts.filter(a => a.platform === 'airbnb' || a.current).map(a => ({
    value: a.key,
    label: accountLabel(a),
    platform: a.platform,
    channelId: a.channelId,
    accountName: a.platform === 'airbnb' ? null : a.name
  })),
  ...OTA_VAT_PLATFORMS.filter(p => p !== 'airbnb').map(p => ({
    value: NEW_ACCOUNT(p),
    label: `حساب ${getPlatformDisplayName(p)} جديد`,
    platform: p,
    channelId: null,
    accountName: null
  }))
];

const AddChangeModal = ({ show, accounts, airbnbNotRead, today, earliest, busy, error, onHide, onSave }) => {
  const options = useMemo(() => accountOptions(accounts), [accounts]);
  const [form, setForm] = useState({ account: '', newName: '', vatStatus: '', date: '', note: '' });

  useEffect(() => {
    if (show) setForm({ account: options[0]?.value || '', newName: '', vatStatus: '', date: today, note: '' });
  }, [show]); // eslint-disable-line react-hooks/exhaustive-deps -- reset on open only

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));
  const chosen = options.find(o => o.value === form.account);
  const isNew = Boolean(chosen && chosen.value.startsWith('new:'));
  const name = form.newName.trim();
  const note = form.note.trim();
  const ready = chosen && form.vatStatus && form.date && form.date >= earliest && form.date <= today
    && note && note.length <= NOTE_MAX
    && (!isNew || (name && name.length <= NAME_MAX));

  const submit = () => onSave({
    platform: chosen.platform,
    ...(chosen.platform === 'airbnb'
      ? { channelId: chosen.channelId }
      : { accountName: isNew ? name : chosen.accountName }),
    vatStatus: form.vatStatus,
    date: form.date,
    note
  });

  return (
    <Modal show={show} onHide={onHide} centered dir="rtl">
      <Modal.Header closeButton><Modal.Title>إضافة تغيير ضريبي</Modal.Title></Modal.Header>
      <Modal.Body>
        <p className="obw-modal-lead">
          سطر لكل تغيير في حالة المالك الضريبية على حساب واحد. يُحفظ عند الضغط على «إضافة»، ويُسحب السطر الخاطئ ولا يُحذف.
        </p>
        {airbnbNotRead && <Alert variant="light" className="obw-ota__inline-note">{OTA_AIRBNB_NOT_READ_NOTE}</Alert>}
        <Form.Group className="mb-3" controlId="otaVatAccount">
          <Form.Label>الحساب</Form.Label>
          <Form.Select value={form.account} onChange={set('account')}>
            {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Form.Select>
        </Form.Group>
        {isNew && (
          <Form.Group className="mb-3" controlId="otaVatAccountName">
            <Form.Label>اسم الحساب</Form.Label>
            <Form.Control value={form.newName} onChange={set('newName')} maxLength={NAME_MAX} />
            <Form.Text>كما يظهر على {getPlatformDisplayName(chosen.platform)}، حتى يُعرف الحساب لاحقًا.</Form.Text>
          </Form.Group>
        )}
        <Form.Group className="mb-3" controlId="otaVatStatus">
          <Form.Label>الحالة الضريبية على هذا الحساب</Form.Label>
          <Form.Select value={form.vatStatus} onChange={set('vatStatus')}>
            <option value="">اختر</option>
            <option value="registered">{OTA_VAT_STATUS_LABELS.registered}</option>
            <option value="not_registered">{OTA_VAT_STATUS_LABELS.not_registered}</option>
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3" controlId="otaVatDate">
          <Form.Label>من تاريخ</Form.Label>
          <Form.Control type="date" value={form.date} min={earliest} max={today} onChange={set('date')} />
        </Form.Group>
        <Form.Group controlId="otaVatNote">
          <Form.Label>الدليل</Form.Label>
          <Form.Control as="textarea" rows={3} value={form.note} onChange={set('note')} maxLength={NOTE_MAX} />
          <Form.Text>{OTA_VAT_NOTE_HINT}</Form.Text>
        </Form.Group>
        {error && <Alert variant="danger" className="mt-3 mb-0">{error}</Alert>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={busy}>إلغاء</Button>
        <Button variant="primary" disabled={busy || !ready} onClick={submit}>إضافة</Button>
      </Modal.Footer>
    </Modal>
  );
};

const WithdrawModal = ({ change, busy, error, onHide, onConfirm }) => {
  const [reason, setReason] = useState('');
  useEffect(() => { if (change) setReason(''); }, [change]);

  return (
    <Modal show={Boolean(change)} onHide={onHide} centered dir="rtl">
      <Modal.Header closeButton><Modal.Title>سحب السطر</Modal.Title></Modal.Header>
      <Modal.Body>
        <p className="obw-modal-lead">
          يبقى السطر ظاهرًا في السجل مشطوبًا مع اسمك والسبب، ولا يُحتسب في الحالة الحالية.
        </p>
        <Form.Group controlId="otaVatWithdrawReason">
          <Form.Label>السبب</Form.Label>
          <Form.Control value={reason} onChange={(e) => setReason(e.target.value)} maxLength={NOTE_MAX} />
        </Form.Group>
        {error && <Alert variant="danger" className="mt-3 mb-0">{error}</Alert>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={busy}>إلغاء</Button>
        <Button variant="danger" disabled={busy || !reason.trim()} onClick={() => onConfirm(reason.trim())}>
          سحب السطر
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const OtaAccountsSection = ({ ownerId, canManage }) => {
  const { data, isLoading, error, refetch } = useOtaAccounts(ownerId);
  const addChange = useAddOtaVatChange(ownerId);
  const withdrawChange = useWithdrawOtaVatChange(ownerId);
  const [showAdd, setShowAdd] = useState(false);
  const [withdrawing, setWithdrawing] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const accounts = data?.accounts || [];
  const unlinked = data?.unlinkedUnits || [];
  // The server's Riyadh "today", read defensively; never the browser's local day.
  const today = data?.defaults?.changeDate || riyadhDayIso(new Date());
  const airbnbNotRead = !accounts.some(a => a.platform === 'airbnb') && unlinked.some(u => u.reason === 'not_read_yet');

  const openAdd = () => { addChange.reset(); setShowAdd(true); };
  const openWithdraw = (change) => { withdrawChange.reset(); setWithdrawing(change); };

  return (
    <section className="obw-card obw-ota">
      <div className="obw-ota__head">
        <h2 className="obw-card__title">{OTA_ACCOUNTS_TITLE}</h2>
        {canManage && data && (
          <Button variant="outline-secondary" size="sm" onClick={openAdd}>إضافة تغيير ضريبي</Button>
        )}
      </div>
      <p className="obw-hint obw-ota__note">{OTA_ACCOUNTS_NOTE}</p>
      {expanded && <p className="obw-hint obw-ota__note">{OTA_ACCOUNTS_DETAIL_NOTE}</p>}

      {isLoading && (
        <div className="obw-empty obw-empty--inline">
          <Spinner animation="border" size="sm" /> <span>جارٍ التحميل…</span>
        </div>
      )}

      {error && !data && (
        <Alert variant="danger" className="obw-alert--retry mb-0">
          <span>{errorText(error, 'تعذّر تحميل حسابات المنصات')}</span>
          <Button variant="outline-danger" size="sm" onClick={() => refetch()}>إعادة المحاولة</Button>
        </Alert>
      )}

      {error && data && (
        <p className="obw-hint obw-missing obw-ota__note">تعذّر تحديث القائمة — المعروض آخر نسخة محمّلة.</p>
      )}

      {data && (
        <>
          {accounts.length === 0 && (
            <div className="obw-ota__empty">
              لا توجد حسابات مسجّلة لهذا المالك بعد.
              {airbnbNotRead && ` ${OTA_AIRBNB_NOT_READ_NOTE}`}
            </div>
          )}

          {accounts.map(a => (
            <Account key={a.key} account={a} expanded={expanded} canManage={canManage} onWithdraw={openWithdraw} />
          ))}

          {expanded && unlinked.length > 0 && (
            <div className="obw-customer__meta obw-ota__unlinked">
              وحدات ليست على حساب Airbnb مقروء:{' '}
              <CodeList items={unlinked.map(u => ({
                key: u.code || u.name,
                code: u.code || u.name,
                suffix: ` (${OTA_UNLINKED_REASON_LABELS[u.reason] || u.reason})`
              }))} />
            </div>
          )}

          {(accounts.length > 0 || unlinked.length > 0) && (
            <button
              type="button"
              className="obw-reopen obw-ota__toggle"
              aria-expanded={expanded}
              onClick={() => setExpanded(v => !v)}
            >
              {expanded ? 'إخفاء التفاصيل' : 'عرض التفاصيل'}
            </button>
          )}
        </>
      )}

      <AddChangeModal
        show={showAdd}
        accounts={accounts}
        airbnbNotRead={airbnbNotRead}
        today={today}
        earliest={data?.defaults?.earliestDate || EARLIEST_DATE}
        busy={addChange.isPending}
        error={addChange.error ? errorText(addChange.error, 'تعذّر حفظ السطر') : null}
        onHide={() => setShowAdd(false)}
        onSave={(change) => addChange.mutate(change, { onSuccess: () => setShowAdd(false) })}
      />
      <WithdrawModal
        change={withdrawing}
        busy={withdrawChange.isPending}
        error={withdrawChange.error ? errorText(withdrawChange.error, 'تعذّر سحب السطر') : null}
        onHide={() => setWithdrawing(null)}
        onConfirm={(reason) => withdrawChange.mutate(
          { changeId: withdrawing.id, reason },
          { onSuccess: () => setWithdrawing(null) }
        )}
      />
    </section>
  );
};

export default OtaAccountsSection;
